import { forwardRef } from "react";
import { Flex } from "../Flex/Flex.js";
import { ICONSAX_SIZE } from "../Iconsax/Iconsax.types.js";
import { Spinner } from "../Spinner/Spinner.js";
import { TextSize, TextWeight } from "../Text/Text.types.js";
import { ChildrenWrapper, LoaderWrapper, StyledButton } from "./Button.style.js";
import type { ButtonProps, DefaultButtonProps } from "./Button.types.js";

/**
 * Default properties for the Button component.
 * @type {DefaultButtonProps}
 */
const defaultProps: DefaultButtonProps = {
    color: "primary",
    size: "medium",
    variant: "fill",
};

/**
 * Mapping of button sizes to corresponding text sizes.
 * @type {Record<ButtonSize, TextSize>}
 */
const buttonTextSizeMap: Record<NonNullable<ButtonProps["size"]>, TextSize> = {
    small: TextSize["XX-SMALL"],
    medium: TextSize["X-SMALL"],
    large: TextSize.SMALL,
};

/**
 * Button component to render a styled button with optional icons, loading state, and customizable properties.
 *
 * @component
 * @param {ButtonProps} props - The properties for the Button component.
 * @param {React.Ref<HTMLButtonElement>} ref - The ref to the button element.
 * @returns {JSX.Element} The rendered Button component.
 *
 * @example
 * ```tsx
 * <Button
 *   color="primary"
 *   size="large"
 *   iconLeft={CrownBold}
 *   loading
 * >
 *   Click Me
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref): JSX.Element => {
    const {
        children,
        size,
        iconLeft: IconLeft,
        iconRight: IconRight,
        loading,
        disabled,
        iconColorOverride,
        whiteSpace = "nowrap",
        ...attributes
    } = { ...defaultProps, ...props };

    const hasIcon = !!(IconLeft || IconRight);
    const hideChildren = !!(loading && !hasIcon);

    return (
        <StyledButton ref={ref} {...attributes} disabled={disabled || loading} size={size}>
            {/* Left Icon or Spinner */}
            {IconLeft && (loading ? <Spinner size="small" /> : <IconLeft size={ICONSAX_SIZE.SMALL} color={iconColorOverride} />)}

            {/* Button Content */}
            {children && (
                <Flex>
                    {/* Loader Wrapper */}
                    {hideChildren && (
                        <LoaderWrapper>
                            <Spinner size={size === "small" ? "small" : "medium"} />
                        </LoaderWrapper>
                    )}

                    {/* Children Wrapper */}
                    <ChildrenWrapper visible={!hideChildren} size={buttonTextSizeMap[size]} weight={TextWeight.BOLD} whiteSpace={whiteSpace}>
                        {children}
                    </ChildrenWrapper>
                </Flex>
            )}

            {/* Right Icon or Spinner */}
            {IconRight && (loading ? <Spinner size="small" /> : <IconRight size={ICONSAX_SIZE.SMALL} color={iconColorOverride} />)}
        </StyledButton>
    );
});
